import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type FormEvent } from 'react'
import { api } from '../lib/api.js'
import { PageHeader } from '../components/PageHeader.js'
import { Section } from '../components/Section.js'
import { Card, CardBody, CardFooter } from '../components/Card.js'
import { DataTable, type Column } from '../components/DataTable.js'
import { Button } from '../components/Button.js'
import { Field, Input } from '../components/Field.js'
import { StatusPill } from '../components/StatusPill.js'

export const Route = createFileRoute('/_app/classes')({
  component: ClassesPage,
})

interface ClassRow {
  id: string
  name: string
  academicYear: string | null
  isActive: boolean
  createdAt: string
}

function ClassesPage() {
  const qc = useQueryClient()
  const navigate = useNavigate()

  const openClass = (c: ClassRow) =>
    navigate({ to: '/attendance/$classId', params: { classId: c.id } })

  const classes = useQuery({
    queryKey: ['classes'],
    queryFn: async () => {
      const res = await api<ClassRow[]>('/api/v1/classes')
      if ('error' in res) throw new Error(res.error.message)
      return res.data
    },
  })

  // Per-class count of students with a school exam in the next 14 days, so each
  // class row can flag upcoming exams before you open it. Only classes with a
  // count are returned; missing → 0.
  const examCounts = useQuery({
    queryKey: ['classes-upcoming-exams'],
    queryFn: async () => {
      const res = await api<{ classId: string; count: number }[]>(
        '/api/v1/student-exams/upcoming-by-class?days=14',
      )
      if ('error' in res) throw new Error(res.error.message)
      return new Map(res.data.map((r) => [r.classId, r.count]))
    },
  })

  const [name, setName] = useState('')
  const [academicYear, setAcademicYear] = useState('')
  const [error, setError] = useState<string | null>(null)

  const create = useMutation({
    mutationFn: async () => {
      const res = await api<ClassRow>('/api/v1/classes', {
        method: 'POST',
        body: JSON.stringify({ name, academicYear: academicYear || undefined }),
      })
      if ('error' in res) throw new Error(res.error.message)
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['classes'] })
      setName('')
      setAcademicYear('')
    },
    onError: (err) => setError(err instanceof Error ? err.message : 'Failed'),
  })

  const onSubmit = (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    create.mutate()
  }

  const columns: Column<ClassRow>[] = [
    {
      key: 'name',
      header: 'Class',
      render: (c) => {
        const examCount = examCounts.data?.get(c.id) ?? 0
        return (
          <span className="flex items-center gap-2">
            <span className="font-medium text-fg">{c.name}</span>
            {examCount > 0 ? (
              <span
                title="Students in this class with a school exam in the next 14 days"
                className="inline-flex items-center rounded-full border border-warning-border bg-warning-tint px-2 py-0.5 text-xs font-medium text-warning"
              >
                {examCount} exam{examCount === 1 ? '' : 's'} soon
              </span>
            ) : null}
          </span>
        )
      },
    },
    {
      key: 'year',
      header: 'Academic year',
      render: (c) => (
        <span className="text-fg-muted">{c.academicYear ?? '—'}</span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (c) => (
        <StatusPill tone={c.isActive ? 'active' : 'cancelled'}>
          {c.isActive ? 'Active' : 'Inactive'}
        </StatusPill>
      ),
    },
    {
      key: 'created',
      header: 'Created',
      align: 'right',
      render: (c) => (
        <span className="text-sm text-fg-muted">
          {new Date(c.createdAt).toLocaleDateString('en-IN')}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      render: (c) => (
        <Link
          to="/attendance/$classId"
          params={{ classId: c.id }}
          className="text-xs font-medium text-[var(--color-primary)] hover:underline"
        >
          Attendance
        </Link>
      ),
    },
  ]

  return (
    <div className="space-y-8">
      <PageHeader
        title="Classes"
        description="Group students by class, batch, or grade. Open a class to mark daily attendance."
      />

      <Section title="Add a class">
        <Card>
          <CardBody>
            <form onSubmit={onSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Class name" required>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  placeholder="Grade 10 — Batch A"
                />
              </Field>
              <Field label="Academic year" optional>
                <Input
                  value={academicYear}
                  onChange={(e) => setAcademicYear(e.target.value)}
                  placeholder="2025-26"
                />
              </Field>
            </form>
          </CardBody>
          <CardFooter className="flex items-center justify-between">
            {error ? <p className="text-sm text-danger">{error}</p> : <span />}
            <Button onClick={() => create.mutate()} loading={create.isPending} disabled={!name}>
              Add class
            </Button>
          </CardFooter>
        </Card>
      </Section>

      <Section title="All classes" description={`${classes.data?.length ?? 0} total`}>
        <DataTable
          columns={columns}
          rows={classes.data ?? []}
          rowKey={(c) => c.id}
          loading={classes.isLoading}
          empty="No classes yet. Add your first one above."
          onRowClick={openClass}
        />
      </Section>
    </div>
  )
}
